import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ArticleCard from "@/components/feature/ArticleCard";
import HotRankingList from "@/components/feature/HotRankingList";
import SectionHeading from "@/components/base/SectionHeading";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

type SortKey = "hot" | "new";

export default function LiveFeedSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [keyword, setKeyword] = useState<string>(searchParams.get("q") ?? "");
  const [sortBy, setSortBy] = useState<SortKey>("hot");
  const [visibleCount, setVisibleCount] = useState<number>(6);

  useEffect(() => {
    setKeyword(searchParams.get("q") ?? "");
  }, [searchParams]);

  const filtered = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    const result = articles.filter((article) => {
      const matchCategory =
        activeCategory === "all" || article.category === activeCategory;
      const haystack = [
        article.title,
        article.summary,
        article.tags.join(" "),
      ]
        .join(" ")
        .toLowerCase();
      const matchKeyword = query.length === 0 || haystack.includes(query);
      return matchCategory && matchKeyword;
    });
    return result.sort((a, b) =>
      sortBy === "hot" ? b.hot - a.hot : b.views - a.views
    );
  }, [activeCategory, keyword, sortBy]);

  const shown = filtered.slice(0, visibleCount);

  const handleKeywordChange = (value: string) => {
    setKeyword(value);
    setVisibleCount(6);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) {
      next.set("q", value.trim());
    } else {
      next.delete("q");
    }
    setSearchParams(next, { replace: true });
  };

  const resetFilters = () => {
    setActiveCategory("all");
    setSortBy("hot");
    handleKeywordChange("");
  };

  return (
    <section
      id="live-feed"
      className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 border-y border-background-200 scroll-mt-24"
    >
      <SectionHeading
        eyebrow="实时爆料"
        title="滚动更新的吃瓜现场"
        desc="按圈层筛选，或直接搜索关键词，快速定位你关心的话题。"
        action={
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-200/70 border border-background-300/60">
            {(
              [
                { key: "hot", label: "最热" },
                { key: "new", label: "最新" },
              ] as { key: SortKey; label: string }[]
            ).map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setSortBy(option.key)}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                  sortBy === option.key
                    ? "bg-background-50 text-primary-600 font-medium"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="flex flex-wrap gap-2 flex-1">
          <button
            type="button"
            onClick={() => {
              setActiveCategory("all");
              setVisibleCount(6);
            }}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors border ${
              activeCategory === "all"
                ? "bg-primary-500 text-background-50 border-primary-500"
                : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-200"
            }`}
          >
            全部瓜田
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              type="button"
              onClick={() => {
                setActiveCategory(cat.slug);
                setVisibleCount(6);
              }}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors border ${
                activeCategory === cat.slug
                  ? "bg-primary-500 text-background-50 border-primary-500"
                  : "bg-background-50 text-foreground-700 border-background-200 hover:border-primary-200"
              }`}
            >
              <i className={cat.icon} aria-hidden="true"></i>
              {cat.name}
            </button>
          ))}
        </div>

        <div className="relative w-full sm:w-64 shrink-0">
          <i
            className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
            aria-hidden="true"
          ></i>
          <input
            type="search"
            value={keyword}
            onChange={(event) => handleKeywordChange(event.target.value)}
            placeholder="搜索瓜、话题、标签"
            aria-label="搜索瓜田内容"
            className="w-full h-11 pl-9 pr-3 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-foreground-500">
              共找到{" "}
              <span className="text-primary-600 font-medium">
                {filtered.length}
              </span>{" "}
              条相关爆料
            </p>
          </div>

          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {shown.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
              {visibleCount < filtered.length && (
                <div className="mt-6 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisibleCount((prev) => prev + 6)}
                    className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-background-50 border border-background-300 text-sm text-foreground-700 whitespace-nowrap hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-down-double-line" aria-hidden="true"></i>
                    加载更多爆料
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-16 px-6 text-center">
              <span className="w-14 h-14 mx-auto rounded-full bg-primary-50 text-primary-500 flex items-center justify-center mb-4">
                <i className="ri-emotion-happy-line text-2xl" aria-hidden="true"></i>
              </span>
              <h3 className="text-base font-medium text-foreground-900">
                这个瓜田暂时还没熟
              </h3>
              <p className="mt-2 text-sm text-foreground-500">
                换个关键词或圈层试试，也可以先看看今日热榜。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 inline-flex items-center gap-1 h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line" aria-hidden="true"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-5">
          <HotRankingList articles={articles} limit={6} />

          <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-5">
            <span className="w-10 h-10 rounded-md bg-secondary-500 text-background-50 flex items-center justify-center mb-3">
              <i className="ri-notification-3-line text-xl" aria-hidden="true"></i>
            </span>
            <h3 className="text-base font-bold text-secondary-950">
              有新瓜第一时间知道
            </h3>
            <p className="mt-2 text-sm text-secondary-900/80 leading-relaxed">
              订阅瓜友会网的每日热点摘要，主编把当天最值得看的几条整理好发给你。
            </p>
            <Link
              to="/about#submit"
              className="mt-4 inline-flex items-center gap-1 h-10 px-4 rounded-full bg-secondary-600 text-background-50 text-sm whitespace-nowrap hover:bg-secondary-700 transition-colors cursor-pointer"
            >
              <i className="ri-mail-add-line" aria-hidden="true"></i>
              去订阅
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}